// Theme preference: light / dark / system, persisted in localStorage.
//
// The applied theme is the `dark` class on <html> (Tailwind darkMode:
// "class"); an inline script in index.html applies it pre-paint to avoid a
// flash of the wrong theme, and this module owns it from then on. "system"
// tracks prefers-color-scheme live via a matchMedia listener.

import { useSyncExternalStore } from "react";

export type ThemePref = "light" | "dark" | "system";

const STORAGE_KEY = "aquatrack-theme";
const THEME_COLOR: Record<"light" | "dark", string> = {
  light: "#f3f5f9",
  dark: "#0b0f14",
};

const listeners = new Set<() => void>();

function readStored(): ThemePref {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "light" || v === "dark" || v === "system") return v;
  } catch {
    // Storage unavailable (private mode) — fall through to system.
  }
  return "system";
}

let pref: ThemePref = readStored();

function systemPrefersDark(): boolean {
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

function apply(): void {
  const dark = pref === "dark" || (pref === "system" && systemPrefersDark());
  document.documentElement.classList.toggle("dark", dark);
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute("content", THEME_COLOR[dark ? "dark" : "light"]);
}

function setThemePref(next: ThemePref): void {
  pref = next;
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // Preference just won't persist across reloads.
  }
  apply();
  listeners.forEach((l) => l());
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

// Follow OS-level changes while in "system"; keep it attached for the app's
// lifetime (module scope), not per-component.
window
  .matchMedia("(prefers-color-scheme: dark)")
  .addEventListener("change", () => {
    if (pref === "system") apply();
  });

// Re-assert on load: the pre-paint script only sets the class; this also
// syncs the theme-color meta.
apply();

export function useTheme(): { pref: ThemePref; setPref: (p: ThemePref) => void } {
  const current = useSyncExternalStore(subscribe, () => pref);
  return { pref: current, setPref: setThemePref };
}
